<script lang="ts" setup>
import { ref, computed, type PropType } from 'vue'
import UpgradeIntermediate from '~/pages/upgrade-intermediate.vue';
const { hasIntermediateAccess } = useAuth();

interface StatusItem {
  timeStamp: string | Date
  holeNo: number
  eventType: string
  amount: number
  startTime: string | Date | null
  criteriaReachedTime: string | Date | null
  stopTime: string | Date | null
  totalTime: string
  totalTimeMs: number
  totalCriteriaTimeMs: number
  totalPumpTimeMs: number
}

const props = defineProps({
  statusData: {
    type: Array as PropType<StatusItem[]>,
    required: true,
    default: () => []
  }
});

const formatDate = (date: any) => {
  if (!date || date === "Not finished") return "--";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "--";
  
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatTime = (date: any) => {
  if (!date || date === "Not finished") return "--:--:--";
  const d = new Date(date);
  // Check if date is valid
  if (isNaN(d.getTime())) return "--:--:--";
  
  return d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const getStatusColor = (status: string) => {
  if (!status) return 'primary';
  const s = status.toLowerCase();
  
  if (s.includes('reached') || s.includes('criteria')) return 'success';
  if (s.includes('not finished') || s.includes('pending')) return 'warning';
  if (s.includes('error') || s.includes('stop') || s.includes('manual')) return 'error';
  
  return 'primary';
};

const formatDurationMs = (ms: number) => {
  const seconds = Math.floor(ms / 1000)
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}



const statusStats = computed(() => {
  const data = props.statusData || [];
  const total = data.length;
  if (total === 0) return { reached: 0, notFinished: 0, reachedPct: 0, notFinishedPct: 0 };

  const reached = data.filter(item => {
    const s = (item.eventType || '').toLowerCase();
    return s.includes('reached') || s.includes('criteria');
  }).length;

  const notFinished = data.filter(item => {
    const s = (item.eventType || '').toLowerCase();
    return s.includes('not finished') || s.includes('pending');
  }).length;

  return {
    reached,
    notFinished,
    reachedPct: (reached / total) * 100,
    notFinishedPct: (notFinished / total) * 100
  };
});

const keyMetrics = computed(() => {
  const data = props.statusData || [];
  if (data.length === 0) return { totalDuration: '00:00:00', avgDuration: '00:00:00' };

  let totalSeconds = 0;
  data.forEach(item => {
    if (item.totalTimeMs && !isNaN(item.totalTimeMs)) {
      totalSeconds += item.totalTimeMs / 1000; // Convert ms to seconds
    }
  });

  const avgSeconds = totalSeconds / data.length;
  
  const formatSec = (sec: number) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  return {
    totalDuration: formatSec(totalSeconds),
    avgDuration: formatSec(avgSeconds)
  };
});

const headers = [
  { title: 'Hole No', key: 'holeNo', align: 'start' as const },
  { title: 'Final Status', key: 'status' },
  { title: 'Start Time', key: 'startTime' },
  { title: 'Crit. Reach Time', key: 'criteriaReachedTime' },
  { title: 'Stop Time', key: 'stopTime' },
  { title: 'Total Duration', key: 'totalTime' },
  { title: 'Crit. Duration', key: 'totalCriteriaTimeMs', align: 'end' as const },
  { title: 'Total Pump Duration', key: 'totalPumpTimeMs', align: 'end' as const },
];
</script>

<template>
  <UpgradeIntermediate v-if="!hasIntermediateAccess" style="min-height: 400px;" />
  <v-container fluid v-else>
    <v-alert
      v-if="!statusData || statusData.length === 0"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      No Status/Hole data available.
    </v-alert>

    <template v-else>
      <v-row>
        <!-- Status Overview Card -->
        <v-col cols="12" md="6">
          <v-card class="h-100" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
              Status Overview
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
               <!-- Progress Bar -->
               <div class="d-flex rounded overflow-hidden mb-6" style="height: 24px; background-color: #f1f5f9;">
                 <div class="bg-success transition-swing" :style="{ width: statusStats.reachedPct + '%' }" title="Reached/Criteria"></div>
                 <div class="bg-warning transition-swing" :style="{ width: statusStats.notFinishedPct + '%' }" title="Not Finished/Pending"></div>
               </div>
               <!-- Legend -->
               <div class="d-flex justify-space-between px-2">
                 <div class="d-flex align-center">
                   <v-badge dot color="success" inline class="mr-2"></v-badge>
                   <span class="text-body-2 text-medium-emphasis">Reached: <strong class="text-high-emphasis">{{ statusStats.reached }}</strong></span>
                 </div>
                 <div class="d-flex align-center">
                   <v-badge dot color="warning" inline class="mr-2"></v-badge>
                   <span class="text-body-2 text-medium-emphasis">Pending: <strong class="text-high-emphasis">{{ statusStats.notFinished }}</strong></span>
                 </div>
               </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Production Summary Card -->
        <v-col cols="12" md="6">
          <v-card class="h-100" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
              Production Summary
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="d-flex justify-space-around align-center py-6">
               <div class="text-center">
                 <div class="text-h4 font-weight-bold text-blue-grey-darken-3">{{ keyMetrics.totalDuration }}</div>
                 <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold mt-1">Total Duration</div>
               </div>
               <v-divider vertical class="my-2"></v-divider>
               <div class="text-center">
                 <div class="text-h4 font-weight-bold text-blue-grey-darken-3">{{ keyMetrics.avgDuration }}</div>
                 <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold mt-1">Avg Duration</div>
               </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Hole Status Details Table -->
      <v-card class="mt-6" elevation="2">
        <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
          <span class="text-subtitle-1 font-weight-bold">Hole Status Details</span>
        </v-card-title>
        <v-divider></v-divider>
        
        <v-data-table
          :headers="headers"
          :items="props.statusData"
          density="compact"
          class="text-body-2"
          hover
        >
          <template v-slot:item.holeNo="{ item }">
            <v-chip size="small" variant="flat" color="blue-lighten-5" class="text-blue-darken-2 font-weight-bold">
              {{ item.holeNo }}
            </v-chip>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.eventType)"
              size="small"
              class="text-uppercase font-weight-bold"
              variant="tonal"
            >
              {{ item.eventType }}
            </v-chip>
          </template>

          <template v-slot:item.startTime="{ item }">
            <div class="d-flex flex-column py-1">
              <span class="text-caption text-medium-emphasis">{{ formatDate(item.startTime) }}</span>
              <span class="text-body-2 font-weight-medium">{{ formatTime(item.startTime) }}</span>
            </div>
          </template>

          <template v-slot:item.criteriaReachedTime="{ item }">
            <div class="d-flex flex-column py-1">
              <span class="text-caption text-medium-emphasis">{{ formatDate(item.criteriaReachedTime) }}</span>
              <span class="text-body-2 font-weight-medium">{{ formatTime(item.criteriaReachedTime) }}</span>
            </div>
          </template>

          <template v-slot:item.stopTime="{ item }">
            <div class="d-flex flex-column py-1">
              <span class="text-caption text-medium-emphasis">{{ formatDate(item.stopTime) }}</span>
              <span class="text-body-2 font-weight-medium">{{ formatTime(item.stopTime) }}</span>
            </div>
          </template>

          <template v-slot:item.totalTime="{ item }">
             <div class="d-flex align-center font-weight-medium">
                <v-icon size="small" class="mr-1 text-medium-emphasis">mdi-timer-outline</v-icon>
                {{ formatDurationMs(item.totalTimeMs) }}
             </div>
          </template>

          <template v-slot:item.totalCriteriaTimeMs="{ item }">
            <span class="font-weight-bold text-high-emphasis">
              <v-icon size="small" class="mr-1 text-medium-emphasis">mdi-timer-outline</v-icon>
              {{ formatDurationMs(item.totalCriteriaTimeMs) }}
            </span>
          </template>

          <template v-slot:item.totalPumpTimeMs="{ item }">
             <div class="d-flex align-center justify-end">
                <v-icon size="small" class="mr-1 text-medium-emphasis">mdi-timer-outline</v-icon>
                {{ formatDurationMs(item.totalPumpTimeMs) }}
             </div>
          </template>
        </v-data-table>
      </v-card>
    </template>
  </v-container>
</template>

<style scoped>
.transition-swing {
  transition: width 0.5s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>